/**
 * @module core/errors
 *
 * Centralised error handling and graceful degradation.
 *
 * Principle: a failure in one visualisation must never take down the page. Each
 * component is initialised through {@link safeInit}, which isolates failures,
 * reports them to the console *and* to the user, and returns a no-op teardown
 * so unmounting stays uniform.
 */

import { notify } from "./toast.js";

/**
 * An error that carries the context of where it happened.
 */
export class AppError extends Error {
  /**
   * @param {string} message
   * @param {Object} [options]
   * @param {string} [options.scope]    Component/module that failed.
   * @param {unknown} [options.cause]   Original error.
   * @param {boolean} [options.userFacing=true] Whether to surface a toast.
   */
  constructor(message, { scope = "app", cause, userFacing = true } = {}) {
    super(message);
    this.name = "AppError";
    this.scope = scope;
    this.cause = cause;
    this.userFacing = userFacing;
  }
}

/** Keeps a record of what failed so the UI (or tests) can query it. */
export const errorLog = [];

/**
 * Report an error: log it with context and optionally tell the user.
 *
 * @param {unknown} error
 * @param {Object} [options]
 * @param {string} [options.scope]
 * @param {string} [options.userMessage]
 * @param {boolean} [options.userFacing=true]
 * @param {"warning"|"error"} [options.severity="error"]
 * @returns {AppError} A normalised error object.
 */
export function reportError(error, options = {}) {
  const {
    scope = "app",
    userMessage,
    userFacing = true,
    severity = "error",
  } = options;

  const normalised =
    error instanceof AppError
      ? error
      : new AppError(error instanceof Error ? error.message : String(error), {
          scope,
          cause: error,
          userFacing,
        });

  errorLog.push({
    scope,
    message: normalised.message,
    at: new Date().toISOString(),
  });

  // Always log the full detail for developers.
  console.error(`[${scope}] ${normalised.message}`, normalised.cause ?? "");

  if (userFacing && document?.body) {
    notify[severity](
      userMessage || "Something went wrong while enhancing this section. Content is still available.",
      { duration: severity === "error" ? 0 : 5000 }
    );
  }

  return normalised;
}

/**
 * Initialise a component safely.
 *
 * The callback may return a teardown function; `safeInit` always returns one so
 * callers can register it without null checks.
 *
 * @param {string} scope Human-readable component name, used in messages.
 * @param {() => (void|(() => void))} initialise
 * @param {Object} [options]
 * @param {string} [options.userMessage]
 * @param {boolean} [options.quiet=false] Suppress the user-facing toast.
 * @returns {() => void} Teardown function (possibly a no-op).
 */
export function safeInit(scope, initialise, options = {}) {
  const { userMessage, quiet = false } = options;
  try {
    const teardown = initialise();
    return typeof teardown === "function" ? teardown : () => {};
  } catch (error) {
    reportError(error, { scope, userMessage, userFacing: !quiet });
    return () => {};
  }
}

/**
 * Wrap a one-off interaction handler so a thrown error cannot break the page or
 * leave the user without feedback.
 *
 * @template {(...args: any[]) => any} F
 * @param {string} scope
 * @param {F} handler
 * @param {Object} [options]
 * @param {string} [options.userMessage]
 * @returns {(...args: Parameters<F>) => ReturnType<F>|undefined}
 */
export function guard(scope, handler, options = {}) {
  return (...args) => {
    try {
      return handler(...args);
    } catch (error) {
      reportError(error, { scope, userMessage: options.userMessage });
      return undefined;
    }
  };
}

/**
 * Install global handlers for uncaught errors and rejections so nothing fails
 * silently.
 *
 * @returns {() => void} Disposer.
 */
export function installGlobalErrorHandlers() {
  if (typeof window === "undefined") return () => {};

  const onError = (event) => {
    reportError(event.error ?? event.message, {
      scope: "window:error",
      userFacing: false,
    });
  };
  const onRejection = (event) => {
    reportError(event.reason, { scope: "window:unhandledrejection", userFacing: false });
  };

  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);

  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  };
}

/**
 * Assert that required elements exist, with a helpful error message.
 *
 * @param {Record<string, Element|null>} nodes
 * @param {string} scope
 * @returns {void}
 */
export function assertNodes(nodes, scope) {
  const missing = Object.entries(nodes)
    .filter(([, node]) => !node)
    .map(([name]) => name);

  if (missing.length) {
    throw new AppError(`Missing required element(s): ${missing.join(", ")}`, {
      scope,
      userFacing: false,
    });
  }
}

/**
 * Return a copy of the error log. Useful for tests and debugging.
 * @returns {Array<{scope: string, message: string, at: string}>}
 */
export function getErrorLog() {
  return [...errorLog];
}

/**
 * Empty the error log. Used by tests and between HMR reloads.
 * @returns {void}
 */
export function clearErrorLog() {
  errorLog.length = 0;
}
